import {
  AMOwnershipGraphVisualResponse,
  AMOwnershipGraphVisualNode,
  AMOwnershipGraphVisualRelation,
  AMOwnershipElementType,
} from 'src/common-services'

export function generatePreloader() {
  return `
  <svg version="1.1" id="L9" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 100 100" xml:space="preserve">
    <path fill="#fff" d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
      <animateTransform
         attributeName="transform"
         attributeType="XML"
         type="rotate"
         dur="1s"
         from="0 50 50"
         to="360 50 50"
         repeatCount="indefinite" />
  </path>
</svg>
  `
}

export const filterDuplicates = (graph: AMOwnershipGraphVisualResponse): AMOwnershipGraphVisualResponse => {
  const uniqueNodesMap = new Map<string, AMOwnershipGraphVisualNode>()
  graph.nodes.forEach((node) => {
    if (!uniqueNodesMap.has(node.id)) {
      uniqueNodesMap.set(node.id, node)
    }
  })

  const uniqueRelationsSet = new Set<string>()
  const uniqueRelations: AMOwnershipGraphVisualRelation[] = []

  graph.relations.forEach((relation) => {
    const relationKey = `${relation.from}-${relation.to}`
    if (!uniqueRelationsSet.has(relationKey)) {
      uniqueRelationsSet.add(relationKey)
      uniqueRelations.push(relation)
    }
  })

  return {
    nodes: Array.from(uniqueNodesMap.values()),
    relations: uniqueRelations,
  }
}

export const sortGraphNodes = (original: AMOwnershipGraphVisualResponse) => {
  const unsorted = { ...original }
  const nodeTypePriority: Record<AMOwnershipElementType, number> = {
    ORGANIZATION: 0,
    PERSON: 1,
    PARCEL: 2,
    UNKNOWN: 3,
  }
  unsorted.nodes.sort((a, b) => {
    if (nodeTypePriority[a.type] !== nodeTypePriority[b.type]) {
      return nodeTypePriority[a.type] - nodeTypePriority[b.type]
    }
    return a.relationsCount - b.relationsCount
  })
  return unsorted
}

export const getNodesToExpand = (graph: AMOwnershipGraphVisualResponse, min: number = 10, max: number = 20) => {
  let count = 0
  const nodesToExpand = []

  for (const node of graph.nodes) {
    if (node.isRoot) {
      continue
    }

    count += node.relationsCount

    if (count < max) {
      nodesToExpand.push(node)
    }

    if (count > max) {
      break
    }
  }

  return nodesToExpand
}

const getNodesByType = (nodes: Array<AMOwnershipGraphVisualNode>, type: AMOwnershipGraphVisualNode['type']) =>
  nodes.filter((node) => node.type === type)

const segmentNodesByType = (nodes: Array<AMOwnershipGraphVisualNode>) =>
  [
    getNodesByType(nodes, 'PARCEL'),
    getNodesByType(nodes, 'PERSON'),
    getNodesByType(nodes, 'ORGANIZATION'),
    getNodesByType(nodes, 'UNKNOWN'),
  ].filter((nodes) => nodes.length)

type GraphVisualNode = AMOwnershipGraphVisualResponse['nodes'][number] &
  Partial<{
    isGroupNode: true
    children: GraphVisual
  }>

export type GraphVisual = {
  nodes: GraphVisualNode[]
  relations: AMOwnershipGraphVisualResponse['relations']
}

export const groupNodes = (parentNodeId: string, graph: AMOwnershipGraphVisualResponse): GraphVisual[] => {
  const { nodes } = graph
  const [firstNode] = nodes
  const { type: groupType } = firstNode
  const newNodeId = `group:${groupType}:${parentNodeId}`

  const nodesByType = segmentNodesByType(graph.nodes)
  return nodesByType.map((nodes) => ({
    nodes: [
      {
        id: newNodeId,
        type: groupType,
        name: `${nodes.length} more`,
        isRoot: false,
        relationsCount: nodes.length,
        isGroupNode: true,
        isDirect: false,
        children: graph as GraphVisual,
      },
    ],
    relations: [
      {
        from: parentNodeId,
        to: newNodeId,
        isDirect: false,
      },
    ],
  }))
}

export const visualToGraph = (graph: GraphVisual): any => ({
  nodes: graph.nodes.map((node) => {
    const nodeConfig: any = {
      id: node.id,
      attributes: {
        text: node.name,
      },
      data: {
        id: node.id,
        type: node.type,
        isGroupNode: node.isGroupNode,
        isDirect: node.isDirect,
        layer: node.layer,
        graph: 'children' in node && node.children ? visualToGraph(node.children) : null,
      },
    }
    if (node.relationsCount > 1 && nodeConfig.attributes) {
      nodeConfig.attributes.badges = {
        topRight: {
          text: node.relationsCount,
        },
      }
    }
    return nodeConfig
  }),
  edges: graph.relations.map((relation) => ({
    id: `${relation.from}-${relation.to}`,
    source: relation.from,
    target: relation.to,
    data: { id: `${relation.from}-${relation.to}`, isDirect: relation.isDirect },
  })),
})

export const nodeGroupsToGraph = (nodeId: string, graph: AMOwnershipGraphVisualResponse): GraphVisual => {
  const newGraph: GraphVisual = { nodes: [], relations: [] }
  const nodeGroups = groupNodes(nodeId, graph)
  for (const { nodes, relations } of nodeGroups) {
    newGraph.nodes = [...newGraph.nodes, ...nodes]
    newGraph.relations = [...newGraph.relations, ...relations]
  }
  return newGraph
}
